import { Alert } from 'bold-ui'
import {
  Chart,
  ChartBody,
  ChartContainer,
  ChartFooter,
  DateRange,
  ReferenceArea,
  SeriesType,
  ValueRange,
} from 'bold-ui/lib/components/Chart'
import { blue, gray, green, red } from 'bold-ui/lib/styles/colors'
import { SexoEnum } from 'graphql/types.generated'
import moment, { Duration, Moment } from 'moment'
import React from 'react'
import { filterGestacoesPeriodo } from 'util/atendimento/gestante/filterGestacoesPeriodo'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { PeriodoGestacaoModel } from '../../../types/PeriodoGestacaoModel'
import { getEndGestacao } from '../../pre-natal/util-medicoesPreNatal'
import { reduceMedicoesIguais } from '../../util-medicoes'
import { LinhaTooltipGrafico } from '../components/LinhaTooltipGrafico'
import { getReferenceAreasCalculators, ReferenceAreasCalculator } from './referenceAreaCalculators'
import { TickIdade } from './TickIdade'

export interface GraficoPesoProps {
  medicoes: HistoricoMedicaoModel[]
  dataNascimento: Moment
  dataRange: DateRange
  sexo: SexoEnum
  gestacoes?: ReadonlyArray<PeriodoGestacaoModel>
  isGestante?: boolean
}

// TODO@CODEBENDERS - Adicionar funcionalidade de outliers #23674
export function GraficoPesoView(props: GraficoPesoProps) {
  const { medicoes, dataNascimento, dataRange, sexo, gestacoes = [], isGestante = false } = props

  const maxIdadeNoRange = moment.duration(moment(dataRange.end).diff(moment(dataNascimento)))
  const medicoesComPeso = reduceMedicoesIguais(medicoes, 'valorPeso') ?? []
  const seriesData = medicoesComPeso.map((m) => ({
    x: m.dataMedicao,
    y: m.valorPeso,
  }))
  const yRange = getYRange(maxIdadeNoRange)
  const gestacoesPeriodo = filterGestacoesPeriodo(gestacoes, moment(dataRange.init), moment(dataRange.end))

  const renderRangeAreas = (gestacoesPeriodo: PeriodoGestacaoModel[]) =>
    gestacoesPeriodo.map((g) => {
      const inicioGestacao = moment(g.inicio).startOf('day')
      const fimGestacao = getEndGestacao(g)

      const inicioDataRange = moment(dataRange.init).startOf('day').add(1, 'day')
      const fimDataRange = moment(dataRange.end).startOf('day')

      const inicioMask = inicioGestacao.isBefore(inicioDataRange) ? inicioDataRange : inicioGestacao
      const fimMask = fimGestacao.isAfter(fimDataRange) ? fimDataRange : fimGestacao

      return {
        name: 'Gestação',
        init: inicioMask,
        end: fimMask,
        fillColor: 'none',
        tickColor: blue.c60,
        strokeColor: gray.c40,
        tooltip: 'O gráfico de peso não deve ser considerado no período da gestação.',
        mask: {
          show: true,
          hideDots: {
            from: inicioMask,
            to: fimMask,
          },
          fillOpacity: 1,
          overlayDots: true,
        },
      }
    })

  return (
    <>
      {isGestante && (
        <Alert type='info' styles={{ container: styles.iconAlertImcGestante }} style={styles.alertImcGestante}>
          O peso não é recomendado como indicador durante a gestação. Utilize o gráfico de ganho de peso para
          monitoramento adequado.
        </Alert>
      )}
      <ChartContainer>
        <ChartBody height={580}>
          <Chart<Moment>
            type={SeriesType.Area}
            series={[{ name: 'Peso', data: seriesData }]}
            referenceAreas={getReferenceAreas(sexo, dataRange, maxIdadeNoRange, dataNascimento)}
            xAxis={{
              title: 'Idade (meses completos e anos)',
              domain: dataRange,
              tickRenderer: (props) => <TickIdade {...props} dataNascimento={dataNascimento} />,
            }}
            rangeAreas={renderRangeAreas(gestacoesPeriodo)}
            yAxis={{ title: 'Peso', unit: 'kg', domain: yRange }}
            showLegend={false}
            tooltip={{
              type: 'point',
              render: (points) => (
                <>
                  {points?.map((p) => (
                    <LinhaTooltipGrafico key={p.y}>{`Peso: ${p.y} kg`}</LinhaTooltipGrafico>
                  ))}
                  <LinhaTooltipGrafico>{`Data: ${moment(points[0].x).format('DD/MM/YYYY')}`}</LinhaTooltipGrafico>
                </>
              ),
            }}
          />
        </ChartBody>
        <ChartFooter>{getFooterText(sexo, maxIdadeNoRange)}</ChartFooter>
      </ChartContainer>
    </>
  )
}

function getFooterText(sexo: SexoEnum, idadeNoRange: Duration): string {
  return idadeNoRange.years() <= 10
    ? 'Fonte: Adaptado do Guia para a Organização da Vigilância Alimentar e Nutricional na Atenção Primária à Saúde, 2023.'
    : ''
}

function getYRange(maxIdadeNoRange: Duration): ValueRange {
  if (maxIdadeNoRange.years() <= 2) return { init: 0, end: 20, step: 1 }
  else if (maxIdadeNoRange.years() <= 5) return { init: 7, end: 30, step: 1 }
  else if (maxIdadeNoRange.years() <= 10) return { init: 10, end: 65, step: 5 }
  else if (maxIdadeNoRange.years() <= 15) return { init: 25, end: 150, step: 5 }
  else return { init: 25, end: 150, step: 5 }
}

function getReferenceAreas(
  sexo: SexoEnum,
  xRange: DateRange,
  idadeNoRange: Duration,
  dataNascimento: Moment
): ReferenceArea<Moment>[] {
  if (idadeNoRange.years() <= 10) {
    const refAreaCalculators = getReferenceAreasCalculators(sexo, dataNascimento, xRange, 'peso')

    return [
      getReferenceAreaMuitoBaixo(refAreaCalculators),
      getReferenceAreaBaixo(refAreaCalculators),
      getReferenceAreaAdequado(refAreaCalculators),
      getReferenceAreaElevado(refAreaCalculators),
    ]
  } else return undefined
}

function getReferenceAreaMuitoBaixo(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(-3),
    })),
    label: {
      name: 'Muito baixo',
    },
    color: gray.c90,
    stroke: {
      color: gray.c40,
    },
    tick: {
      color: gray.c40,
    },
  }
}

function getReferenceAreaBaixo(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(-2),
    })),
    label: {
      name: 'Baixo',
    },
    color: red.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaAdequado(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(2),
    })),
    label: {
      name: 'Adequado',
    },
    color: green.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: green.c60,
    },
  }
}

function getReferenceAreaElevado(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date }) => ({
      x: date,
    })),
    label: {
      name: 'Elevado',
    },
    color: red.c90,
    tick: {
      color: red.c60,
    },
  }
}

const styles = {
  alertImcGestante: {
    marginBottom: '1rem',
  },
  iconAlertImcGestante: {
    svg: {
      fontSize: '1rem',
    },
  },
}
